'use client';

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { Connection, connectionsApi } from '@/app/api/connections';
import { articlesApi } from '@/app/api/articles';
import { useToast } from '@/hooks/useToast';
import { blogSpotPublishingService, BlogSpotConnection } from '@/services/blogspot-publishing';
import { ContentConverter } from '@/utils/contentConverter';
import { mediumPublishingService } from '@/services/medium-publishing';
import { customHttpPublishingService } from '@/services/custom-http-publishing';
import PublishingModal from './PublishingModal';

interface PublishToDropdownProps {
  articleId: string;
  onPublishComplete?: () => void;
}

export default function PublishToDropdown({ articleId, onPublishComplete }: PublishToDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPublishing, setIsPublishing] = useState<Record<string, boolean>>({});
  const [isPublishingModalOpen, setIsPublishingModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  // Load active connections when component mounts
  useEffect(() => {
    const loadConnections = async () => {
      try {
        setIsLoading(true);
        const activeConnections = await connectionsApi.getActive();
        setConnections(activeConnections);
      } catch (error) {
        console.error('Error loading connections:', error);
        addToast('Failed to load connections', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadConnections();
  }, [addToast]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getConnectionIcon = (connection: Connection) => {
    if (connection.iconUrl) {
      return (
        <img 
          src={connection.iconUrl} 
          alt={connection.platformName}
          className="w-4 h-4"
        />
      );
    }
    return <GlobeAltIcon className="w-4 h-4" />;
  };

  const [selectedConnectionForModal, setSelectedConnectionForModal] = useState<string>('');

  const handleDirectPublish = (connectionId: string) => {
    setIsOpen(false);
    setSelectedConnectionForModal(connectionId);
    setIsPublishingModalOpen(true);
  };

  const handlePublishModalSubmit = async (connectionIds: string[]) => {
    // This will be handled by the PublishingModal itself
    if (onPublishComplete) {
      onPublishComplete();
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedConnectionForModal('');
    setIsPublishingModalOpen(true);
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className="relative inline-block text-left" ref={dropdownRef}>
        <div className="inline-flex rounded-md shadow-sm">
          <button
            onClick={handleButtonClick}
            className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-l-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isLoading}
          >
            <GlobeAltIcon className="h-4 w-4 mr-1" />
            Publish To
          </button>
          <button
            onClick={handleDropdownToggle}
            className="inline-flex items-center px-2 py-1.5 border border-l-0 border-transparent shadow-sm text-sm font-medium rounded-r-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isLoading}
          >
            <ChevronDownIcon className="h-4 w-4" />
          </button>
        </div>

        {isOpen && (
          <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
            <div className="py-1">
              {isLoading ? (
                <div className="px-4 py-2 text-sm text-gray-500">Loading connections...</div>
              ) : (
                <>
                  {connections.length > 0 ? (
                    connections.map((connection) => (
                      <button
                        key={connection.id}
                        onClick={() => handleDirectPublish(connection.id)}
                        disabled={isPublishing[connection.id]}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center">
                          {getConnectionIcon(connection)}
                          <span className="ml-2">{connection.platformName}</span>
                          {isPublishing[connection.id] && (
                            <div className="ml-auto">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No active connections found.{' '}
                      <a href="/user/connections" className="text-indigo-600 hover:text-indigo-500">
                        Add connections
                      </a>
                    </div>
                  )}
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={() => handleDirectPublish('manual')}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <GlobeAltIcon className="w-4 h-4 text-gray-500" />
                    <span className="ml-2 font-medium">Manual Publish</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <PublishingModal 
        isOpen={isPublishingModalOpen}
        onClose={() => {
          setIsPublishingModalOpen(false);
          setSelectedConnectionForModal('');
        }}
        onPublish={handlePublishModalSubmit}
        isPublishing={false}
        articleId={articleId}
        preselectedConnectionId={selectedConnectionForModal || undefined}
      />
    </>
  );
}
